import { Component, ReactNode } from "react";
import { ReportRenderer } from "./widgets/ReportRenderer";
import { BiReport } from "../../models/BiReport";
import request from "superagent";
import { BiBasicFilter } from "../../models/BiBasicFilter";
import { Loader } from "@screencloud/screencloud-ui-components";
import * as Sentry from "@sentry/browser";
import { BiReportPageSettings } from "../../models/BiReportPageSettings";
import jwt_decode from "jwt-decode";
import { ScreenCloudLogger } from "../../ScreenCloudReactApp";
import { v4 as uuidv4 } from "uuid";
import { Hub, BrowserClient } from "@sentry/browser";
import { JWTPayload } from "../../types/JWTPayload";

interface State {
  isLoading: boolean;
  report?: BiReport | null;
  appConfigId: string;
  credentialId: string;
  reportId: string;
  reportType: string;
  workspaceId: string;
  filters: BiBasicFilter[];
  pageSettings: BiReportPageSettings;
  reloadCount: number;
  urlFilter?: string;
  refreshMilliseconds: number;
}

interface Props {
  reportId: string;
  appConfigId: string;
  credentialId: string;
  reportType: string;
  workspaceId: string;
  filters: BiBasicFilter[];
  pageSettings: BiReportPageSettings;
  orgId: string;
  urlFilter?: string;
  refreshMilliseconds?: number;
  appStarted?: boolean;
  logger: ScreenCloudLogger | undefined;
  appViewerToken?: string;
  appInstanceId: string;
}

const REFRESH_REPORT_INTERVAL_BEFORE_EXPIRY_MILLISECONDS_DEFAULT = 300000; // 5 mins before expiry

/**
 * Interval for setInterval.
 *
 * @description
 * This is the main setInterval invoked upon componentDidMount to refresh data and reports as needed.
 *
 * @remarks
 * This value should match the minimum increment for the data refresh rate which is currently in minutes (with a minimum
 * of 5). Any larger than this could introduce delays to refreshes and any shorter interval is probably wasteful.
 */
const CHECK_EXPIRY_RATE_MILLISECONDS = 60000; // 1 minute
/**
 * Default timeout for refreshing reports.
 *
 * @remarks
 * Users have the option to set this duration directly via the editor, choosing intervals in hours and minutes, subject
 * to a minimum of 5 minutes. In cases where this setting is absent (such as in older versions), it defaults to the
 * value specified here.
 */
const REFRESH_DATA_INTERVAL_MILLISECONDS_DEFAULT = 300000;

const NO_APP_VIEWER_TOKEN_ERROR_MSG = "No App Viewer token available";
const EXPIRED_TOKEN_ERROR_MSG = "Expired App Viewer token found";

const SENTRY_APP_LABEL = "power-bi";
export class PowerBiContainer extends Component<Props, State> {
  authTokenAttempts = 0;
  authTokenTimeout?: number;
  private authToken?: string;
  private tokenExpiry?: number;
  private lastUpdateReportTime = 0;
  private lastRefreshDataTime = 0;
  public tokenHub: Sentry.Hub;

  /*- - - - - - - - - - - - - -
     * CTOR
     - - - - - - - - - - - - - -*/
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      report: null,
      reportId: props.reportId,
      reportType: props.reportType,
      appConfigId: props.appConfigId,
      credentialId: props.credentialId,
      workspaceId: props.workspaceId,
      filters: props.filters,
      pageSettings: props.pageSettings,
      urlFilter: props.urlFilter,
      refreshMilliseconds:
        props.refreshMilliseconds || REFRESH_DATA_INTERVAL_MILLISECONDS_DEFAULT,
      reloadCount: 0,
    };
    this.lastRefreshDataTime = Date.now();
    this.authToken = props.appViewerToken;

    /*
     * temporary to help debug Client_TokenExpired errors
     */
    if (props.appViewerToken) {
      const decodedToken = jwt_decode<JWTPayload>(props.appViewerToken);
      const tokenExpiry = decodedToken.exp;
      this.props.logger?.info(
        `PowerBI loaded with appViewerToken having expiry ${tokenExpiry}`
      );
    }

    if (!props.appViewerToken) {
      // No appViewerToken has been sent down from the studio player
      this.props.logger?.error(`PowerBI loaded with appViewerToken undefined`, {
        appViewerToken: props.appViewerToken,
      });
    }

    this.tokenHub = new Hub(
      new BrowserClient({
        dsn: process.env.REACT_APP_DSN_TOKEN,
        environment: process.env.REACT_APP_ENVIRONMENT,
        enabled: process.env.REACT_APP_ENVIRONMENT !== "dev", // Disable in development
      })
    );
  }

  sendErrorToSentryForToken = (
    error: string,
    token?: JWTPayload,
    tokenExpiry?: number,
    currentTime?: number
  ): void => {
    if (process.env.REACT_APP_ENVIRONMENT) {
      this.tokenHub.setTags({
        report_type: this.state.reportType,
        report_id: this.state.reportId,
        workspace_id: this.state.workspaceId,
        app_config_id: this.state.appConfigId,
        credential_id: this.state.credentialId,
        org_id: this.props.orgId,
        token_present: !!token,
        viewer_token_expiry: tokenExpiry,
        current_time: currentTime,
        iss: token ? token.iss : undefined,
        aud: token ? token.aud : undefined,
        app: SENTRY_APP_LABEL,
      });

      this.tokenHub.captureException(new Error(error));
    }
  };

  sendErrorToSentry = (
    error: Error | string,
    detailedError?: string,
    correlationId?: string
  ): void => {
    if (process.env.REACT_APP_ENVIRONMENT) {
      Sentry.withScope((scope) => {
        scope.setTag("report_type", this.state.reportType);
        scope.setTag("report_id", this.state.reportId);
        scope.setTag("workspace_id", this.state.workspaceId);
        scope.setTag("app_config_id", this.state.appConfigId);
        scope.setTag("credential_id", this.state.credentialId);
        scope.setTag("org_id", this.props.orgId);

        if (detailedError) {
          scope.setTag("detailed_error", String(detailedError));
        }

        if (correlationId) {
          scope.setTag("correlationId", correlationId);
        }

        Sentry.captureException(
          typeof error === "string" ? new Error(error) : error
        );
      });
    }
  };

  /*- - - - - - - - - - - - - -
     * COMPONENT MOUNTED
     - - - - - - - - - - - - - -*/
  async componentDidMount(): Promise<void> {
    console.log(
      `refreshMilliseconds set to ${this.state.refreshMilliseconds} milliseconds.`
    );
    try {
      if (this.state.reportId !== undefined) {
        await this.fetchData();
        setInterval(async () => {
          console.log("Checking if need to fetch data: " + new Date());

          /**
           * Handle report reload and data refreshing
           *
           * @remarks
           * With the removal of Runtime tokens we should now only need to deal with App Viewer tokens.
           * There is no longer the notion of refreshing the auth token so we should always have a valid
           * App Viewer token unless the Player has fallen asleep at the wheel. Even without a daily re-start
           * the token refresh logic in the player should refresh the token every 6 hours.
           *
           * We can shorten the interval between expiry checks as we are only either refreshing the whole report
           * which will pull in new data or just refreshing the data or doing nothing. If we shorten this too much
           * it will just add unecessary processing so I think once per minute is a sensible number.
           */

          /**
           * To mitigate client token expired errors we are going to switch to just reloading the report details
           * 5 minutes before the token expiry. This will also reduce the number of calls overall.
           * We need to adjust the tokenExpiry which is in seconds from epoch to millis to match the current timestamp
           */
          if (
            !this.tokenExpiry ||
            (this.tokenExpiry &&
              this.tokenExpiry * 1000 - Date.now() <
                REFRESH_REPORT_INTERVAL_BEFORE_EXPIRY_MILLISECONDS_DEFAULT)
          ) {
            console.log("Fetching data: " + new Date());
            await this.fetchData();
          }
          if (
            this.lastRefreshDataTime <
            Date.now() - this.state.refreshMilliseconds
          ) {
            this.lastRefreshDataTime = Date.now();
            // just reload the data
            this.setState({
              reloadCount: this.state.reloadCount + 1,
            });
          }
        }, CHECK_EXPIRY_RATE_MILLISECONDS);
      }
    } catch (error) {
      const message =
        error.message || "PowerBI Container error during mounting";
      console.log("Error during mounting:", error);
      this.props.logger?.error(message);
      this.sendErrorToSentry(error);
      this.setState(() => {
        throw error;
      });
    }
  }

  componentWillUnmount(): void {
    if (this.authTokenTimeout) {
      window.clearTimeout(this.authTokenTimeout);
    }
  }

  async componentDidUpdate(prevProps: Props): Promise<void> {
    console.log(
      "viewer - PowerBiContainer - componentDidUpdate - this.props.reportId: ",
      this.props.reportId
    );

    if (prevProps.appViewerToken !== this.props.appViewerToken) {
      this.authToken = this.props.appViewerToken;

      /*
       * temporary to help debug Client_TokenExpired errors
       * token has changed so log new expiry
       */
      if (this.props.appViewerToken) {
        const decodedToken = jwt_decode<JWTPayload>(this.props.appViewerToken);
        const tokenExpiry = decodedToken.exp;
        this.props.logger?.info(
          `PowerBI updated appViewerToken with new token having expiry ${tokenExpiry}`
        );
      }
    }

    // componentDidMount is only called on initial preview
    // Make sure fetchData() is called on every preview, not just initial one
    // to fetch up-to-date report values
  }

  /*- - - - - - - - - - - - - -
     * FETCH REPORT DATA
     - - - - - - - - - - - - - -*/
  private fetchData = async (): Promise<void> => {
    console.log("viewer - PowerBiContainer - calling fetchData()");

    //dont show spinner if we have report on screen
    if (!this.state.report) {
      this.setState({ isLoading: true });
    }

    const correlationId = uuidv4();

    try {
      const authToken = await this.getAuthToken();

      if (!authToken || authToken.trim() === "") {
        console.log("Auth token is undefined or empty:", authToken);
        throw new Error(`Auth token is undefined or empty`);
      }

      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

      if (!apiBaseUrl || apiBaseUrl.trim() === "") {
        console.log("Unable to get the base api url");
        throw new Error(`Unable to get the base api url`);
      }

      const { appConfigId, workspaceId } = this.state;

      if (!appConfigId) {
        throw new Error("appConfigId is required. Unable to continue.");
      }

      /**
       * Determine the appropriate URL.
       *
       * @remarks
       * Although neither URL includes the workspaceId as a path parameter, the handlers function differently. One will
       * require and retrieve the workspaceId from Connections in the backend, while the other will ignore it. This
       * distinction is crucial, as some configurations do not have a workspaceId, so we cannot assume its presence.
       */
      const reportUrl = workspaceId
        ? `${apiBaseUrl}/${appConfigId}/workspace/report`
        : `${apiBaseUrl}/${appConfigId}/report`;

      // Log this data out to determine if the app is behaving.
      console.log("workspaceId: ", workspaceId);
      console.log(`reportUrl: `, reportUrl);

      console.log(
        "fetchData: Making request with correlationId:",
        correlationId
      );

      /**
       * Retrieves the report details.
       *
       * @remarks
       * The appConfigId is now sufficient to fetch the report details. We utilise the /workspace/report endpoint
       * instead of the /report endpoint. This is for backward compatibility purposes as the `Sc-Correlation-Id`
       * header is only supported by the former.
       *
       * @todo
       * It might be unnecessary to continue using the Sc-Correlation-Id. Consider removing this header from the
       * application.
       */
      const response = await request
        .get(reportUrl)
        .set("Authorization", `Bearer ${authToken}`)
        .set("Sc-Correlation-Id", correlationId)
        .set("App-Instance-Id", this.props.appInstanceId)
        .retry(2)
        .timeout(20000);
      const report = response.body;

      this.props.logger?.info(
        `PowerBI Report - report fetched with correlationId: ${correlationId}`
      );

      console.log(
        "viewer - PowerBiContainer - returned config from DB: ",
        report
      );

      //only reload when something we care about changes else check if data needs refreshed
      if (
        !this.state.report ||
        this.state.report.type !== report.type ||
        this.state.report.token !== report.token ||
        this.state.report.embeddedUrl !== report.embeddedUrl ||
        this.state.report.reportId !== report.reportId
      ) {
        // Token contains a lot more than this, but not clear how/if it's used and tokens will change soon, so just type def'd what is used.
        const decodedToken = jwt_decode<{ exp: number }>(report.token);
        this.tokenExpiry = decodedToken.exp;

        this.props.logger?.info(
          `PowerBI Report - fetched report has changed - tokenExpiry ${decodedToken.exp} with correlationId: ${correlationId}`
        );

        this.setState({ report, isLoading: false });
      }

      if (
        this.lastRefreshDataTime <
        Date.now() - this.state.refreshMilliseconds
      ) {
        this.lastRefreshDataTime = Date.now();
        // just reload the data
        this.setState({
          reloadCount: this.state.reloadCount + 1,
        });
      }

      this.lastUpdateReportTime = Date.now();
    } catch (err) {
      console.log(err);

      if (err.timeout) {
        console.log("Report timed out");
        //Datadog log
        this.props.logger?.error(
          `PowerBI Container Report timed out ${err} with correlationId: ${correlationId}`
        );
        this.setState({ isLoading: false });
        this.sendErrorToSentry(err, undefined, correlationId);
        return;
      }

      // Check if report not found (can happen if report removed my user on power bi)
      if (err && err.status && err.status === 404) {
        console.log("report not found");
        //Datadog log
        this.props.logger?.error(
          `PowerBI Container Report not found 404 error ${err} with correlationId: ${correlationId}`
        );
        this.setState({ isLoading: false });
        return;
      }
      //Datadog log
      this.props.logger?.error(
        `PowerBI Container error retrieving report ${err} with correlationId: ${correlationId}`
      );
      console.log("Error retrieving report", err);
      this.sendErrorToSentry(err, undefined, correlationId);
      this.setState(() => {
        throw err;
      });
    }
  };

  /*- - - - - - - - - - - - - -
     * RENDER
     - - - - - - - - - - - - - -*/
  render(): ReactNode {
    if (this.state.reportId === undefined) {
      return (
        <div className="report-not-found-holder">
          <img src="img/power-bi-icon.png" alt="power bi icon" />
          <h1>No Report Selected</h1>
          <p>Please login to Studio and setup your report</p>
          <h4>{this.state.reportId}</h4>
        </div>
      );
    } else if (this.state.isLoading === true) {
      return (
        <div className="loader-container">
          <div className="loader-wrapper">
            <Loader inline size="medium" active />
            <p style={{ marginTop: 16 }}>Loading</p>
          </div>
        </div>
      );
    } else if (this.state.report) {
      console.log(
        "viewer - PowerBiContainer - render - this.state.report: ",
        this.state.report
      );

      return (
        <ReportRenderer
          report={this.state.report}
          filters={this.state.filters}
          pageSettings={this.state.pageSettings}
          appStarted={this.props.appStarted}
          reloadCount={this.state.reloadCount}
          urlFilter={this.state.urlFilter}
          sendErrorToSentry={this.sendErrorToSentry}
          logger={this.props.logger}
        />
      );
    } else {
      return (
        <div className="report-not-found-holder">
          <img src="img/power-bi-icon.png" alt="power bi icon" />
          <h1>Report Not Found</h1>
          <h4>{this.state.reportId}</h4>
        </div>
      );
    }
  }

  getAuthToken = async (): Promise<string | undefined> => {
    if (this.authToken) {
      const decodedToken = jwt_decode<JWTPayload>(this.authToken);
      const tokenExpiry = decodedToken.exp;
      const currentTime = Math.round(Date.now() / 1000);

      // if authToken expiry in the future return existing token
      if (tokenExpiry && tokenExpiry > currentTime) {
        console.log("Returning cached authToken");
        return this.authToken;
      } else {
        // tell sentry that we have an expired token
        this.sendErrorToSentryForToken(
          EXPIRED_TOKEN_ERROR_MSG,
          decodedToken,
          tokenExpiry,
          currentTime
        );
        this.props.logger?.error("appViewerToken token has expired", {
          expiry: tokenExpiry,
          audience: decodedToken.aud,
        });
      }
    } else {
      // tell sentry that we don't have a valid token available
      this.sendErrorToSentryForToken(NO_APP_VIEWER_TOKEN_ERROR_MSG);
    }
  };

  wait = (ms: number): Promise<undefined> => {
    return new Promise(
      (resolve, reject) =>
        (this.authTokenTimeout = window.setTimeout(resolve, ms))
    );
  };
}
